/**
 * CreateTripPage — driver creates a trip with route + schedule + vehicle.
 * Mirrors Flutter `create_trip_page.dart`. Map picker lands in Phase 7.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Truck, MapPin, Calendar, DollarSign } from 'lucide-react';
import { useCreateTrip } from '@/hooks/useTrips';
import { useMyVehicles } from '@/hooks/useVehicles';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';

export default function CreateTripPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateTrip();
  const { data: vehicles = [] } = useMyVehicles();

  const [vehicleId, setVehicleId] = useState<number | undefined>(undefined);
  const [originCity, setOriginCity] = useState('');
  const [destinationCity, setDestinationCity] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [maxWeightKg, setMaxWeightKg] = useState('');
  const [pricePerKg, setPricePerKg] = useState('');
  const [currency, setCurrency] = useState<'USD' | 'SYP'>('USD');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!originCity.trim() || !destinationCity.trim()) {
      setError(t('err_route_required', 'حدّد المدينتين')); return;
    }
    if (!departureDate) {
      setError(t('err_date_required', 'حدّد تاريخ المغادرة')); return;
    }
    try {
      const trip = await create.mutateAsync({
        vehicleId,
        originCity: originCity.trim(),
        destinationCity: destinationCity.trim(),
        departureDate,
        maxWeightKg: maxWeightKg ? parseFloat(maxWeightKg) : undefined,
        pricePerKg: pricePerKg ? parseFloat(pricePerKg) : undefined,
        currency,
        notes: notes.trim() || undefined,
      });
      navigate(`/trips/${trip.id}`, { replace: true });
    } catch (err: any) {
      setError(err?.message || t('create_failed', 'فشل الإنشاء'));
    }
  }

  return (
    <AppLayout>
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary">
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="bg-surface border border-border rounded-2xl p-6 max-w-xl mx-auto">
        <h1 className="text-xl font-bold text-primary mb-5 flex items-center gap-2">
          <Truck size={20} /> {t('new_trip', 'رحلة جديدة')}
        </h1>

        {error && (
          <div className="bg-error/10 text-error rounded-xl p-3 text-sm mb-4">{error}</div>
        )}

        <form onSubmit={submit} className="space-y-4">
          {vehicles.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                {t('vehicle', 'المركبة')}
              </label>
              <select
                value={vehicleId ?? ''}
                onChange={(e) => setVehicleId(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                className="w-full rounded-xl border border-border bg-surface px-4 py-3 text-text-primary outline-none focus:border-primary"
              >
                <option value="">{t('no_vehicle', 'بدون تحديد مركبة')}</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.brand} {v.model} — {v.plate_number}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <TextField
              label={<span className="flex items-center gap-1"><MapPin size={14} /> {t('from', 'من')}</span> as any}
              value={originCity}
              onChange={(e) => setOriginCity(e.target.value)}
              autoFocus
            />
            <TextField
              label={<span className="flex items-center gap-1"><MapPin size={14} /> {t('to', 'إلى')}</span> as any}
              value={destinationCity}
              onChange={(e) => setDestinationCity(e.target.value)}
            />
          </div>

          <TextField
            label={<span className="flex items-center gap-1"><Calendar size={14} /> {t('departure_date', 'تاريخ المغادرة')}</span> as any}
            type="datetime-local"
            value={departureDate}
            onChange={(e) => setDepartureDate(e.target.value)}
          />

          <div className="grid grid-cols-2 gap-3">
            <TextField
              label={t('max_weight_kg', 'أقصى حمولة (كغ)')}
              type="number"
              dir="ltr"
              value={maxWeightKg}
              onChange={(e) => setMaxWeightKg(e.target.value)}
            />
            <TextField
              label={<span className="flex items-center gap-1"><DollarSign size={14} /> {t('price_per_kg', 'السعر/كغ')}</span> as any}
              type="number"
              step="0.01"
              dir="ltr"
              value={pricePerKg}
              onChange={(e) => setPricePerKg(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {t('currency', 'العملة')}
            </label>
            <div className="flex gap-2">
              {(['USD', 'SYP'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCurrency(c)}
                  className={`px-4 py-2 rounded-xl border text-sm transition ${
                    currency === c
                      ? 'bg-primary text-white border-primary'
                      : 'bg-surface text-text-secondary border-border hover:border-primary'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <TextField
            label={t('notes', 'ملاحظات (اختياري)')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          <Button type="submit" loading={create.isPending} fullWidth>
            {t('create_trip', 'إنشاء الرحلة')}
          </Button>
        </form>
      </div>
    </AppLayout>
  );
}
